import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  createBoundedNameModel,
  BoundedNameModel,
} from "../../models/BoundedNameModel";
import {
  createKeycloakEmailModel,
  KeycloakEmailModel,
} from "../../models/KeycloakEmailModel";
import {
  createEmailContentModel,
  EmailContentModel,
} from "../models/EmailContentModel";
import {
  createRecurringScheduleModel,
  RecurringScheduleModel,
} from "../models/RecurringScheduleModel";
import {
  createSingleTimeScheduleModel,
  SingleTimeScheduleModel,
} from "../models/SingleTimeScheduleModel";
import {
  createImmediateScheduleModel,
  ImmediateScheduleModel,
} from "../models/ImmediateScheduleModel";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { GetEmailCycleDetailsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailCycleDetailsRPC";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { getAPIClient } from "../../networking/APIClient";
import { useGetEmailCycleDetailsRPCClientImpl } from "../rpcs/RPC";
import { EmailCycleStatus } from "@pulse/pulse-rpcs/lib/email-cycle/emailCycleStatus";
import { ScheduleType } from "@pulse/pulse-rpcs/lib/email-cycle/scheduleType";
import {
  createUpdateEmailCycleStore,
  UpdateEmailCycleStore,
} from "./UpdateEmailCycleStore";
import {
  createDeleteEmailCycleStore,
  DeleteEmailCycleStore,
} from "./DeleteEmailCycleStore";
import {
  createEmailCycleRecipientsStore,
  EmailCycleRecipientsStore,
} from "./EmailCycleRecipientsStore";
import {
  createPreviewEmailStore,
  PreviewEmailStore,
} from "./PreviewEmailStore";
import {
  createGetInvestigationSummaryStore,
  GetInvestigationSummaryStore,
} from "./GetInvestigationSummaryStore";
import { createResendEmailStore, ResendEmailStore } from "./ResendEmailStore";

const getSingleTimeScheduleSnapshot = (
  scheduleType: ScheduleType.SingleTime,
): SnapshotIn<typeof SingleTimeScheduleModel> => ({
  type: "SINGLE_TIME_SCHEDULE",
  date: {
    dateString: scheduleType.date.date,
  },
  time: {
    hour: scheduleType.time.hour,
    minute: scheduleType.time.minute,
  },
});

const getRecurringScheduleSnapshot = (
  scheduleType: ScheduleType.Recurring,
): SnapshotIn<typeof RecurringScheduleModel> => ({
  type: "RECURRING_SCHEDULE",
  startDate: {
    dateString: scheduleType.startDate.date,
  },
  endDate: {
    dateString: scheduleType.endDate.date,
  },
  daysOfWeek: {
    value: scheduleType.daysOfWeek,
  },
  timeOfDay: {
    hour: scheduleType.timeOfDay.hour,
    minute: scheduleType.timeOfDay.minute,
  },
});

const getImmediateScheduleSnapshot = (): SnapshotIn<
  typeof ImmediateScheduleModel
> => ({
  type: "IMMEDIATE_SCHEDULE",
});

const getInitialState = (): SnapshotIn<typeof EmailCycleDetailsStore> => ({
  scheduleId: EMPTY_CHARACTER,
  cycleName: createBoundedNameModel(),
  displayCycleName: createBoundedNameModel(),
  senderEmail: createKeycloakEmailModel(),
  senderName: createBoundedNameModel(),
  emailContent: createEmailContentModel(),
  scheduleType: createSingleTimeScheduleModel(),
  status: EmailCycleStatus.EmailCycleStatus.QUEUED,
  scheduledAt: EMPTY_CHARACTER,
  loadingEmailCycleDetails: false,
  getEmailCycleDetailsRPCError: null,
  updateEmailCycleStore: createUpdateEmailCycleStore(),
  deleteEmailCycleStore: createDeleteEmailCycleStore(),
  emailCycleRecipientsStore: createEmailCycleRecipientsStore(),
  previewEmailStore: createPreviewEmailStore(),
  getInvestigationSummaryStore: createGetInvestigationSummaryStore(),
  resendEmailStore: createResendEmailStore(),
  isScheduleEditable: false,
});

export const EmailCycleDetailsStore = types
  .model("EmailCycleDetailsStore", {
    scheduleId: types.string,
    cycleName: BoundedNameModel,
    displayCycleName: BoundedNameModel,
    senderEmail: KeycloakEmailModel,
    senderName: BoundedNameModel,
    emailContent: EmailContentModel,
    scheduleType: types.union(
      SingleTimeScheduleModel,
      RecurringScheduleModel,
      ImmediateScheduleModel,
    ),
    status: types.enumeration(
      "EmailCycleDetailsStatus",
      Object.values(EmailCycleStatus.EmailCycleStatus),
    ),
    scheduledAt: types.string,
    isScheduleEditable: types.boolean,
    loadingEmailCycleDetails: types.boolean,
    getEmailCycleDetailsRPCError: types.maybeNull(
      types.enumeration(
        "GetEmailCycleDetailsRPCError",
        Object.values(GetEmailCycleDetailsRPC.RPCError),
      ),
    ),
    updateEmailCycleStore: UpdateEmailCycleStore,
    deleteEmailCycleStore: DeleteEmailCycleStore,
    emailCycleRecipientsStore: EmailCycleRecipientsStore,
    previewEmailStore: PreviewEmailStore,
    getInvestigationSummaryStore: GetInvestigationSummaryStore,
    resendEmailStore: ResendEmailStore,
  })
  .actions((store) => ({
    getEmailCycleDetails: flow(function* (
      scheduleId: string,
      syncUpdateStore = false,
    ) {
      try {
        store.loadingEmailCycleDetails = true;
        store.getEmailCycleDetailsRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new GetEmailCycleDetailsRPC.Request(
          new LeoUUID(scheduleId),
        );

        const result: LeoRPCResult<
          GetEmailCycleDetailsRPC.Response,
          GetEmailCycleDetailsRPC.Errors.Errors
        > =
          yield useGetEmailCycleDetailsRPCClientImpl(apiClient).execute(
            request,
          );

        if ("error" in result) {
          switch (result.error.code) {
            case GetEmailCycleDetailsRPC.RPCError.InvalidScheduleId:
              store.getEmailCycleDetailsRPCError = result.error.code;
              break;
            default:
              result.error.code satisfies never;
          }
          return;
        }

        const { emailCycleDetails } = result.response;
        store.scheduleId = emailCycleDetails.id.uuid;
        store.cycleName.setName(emailCycleDetails.cycleName.name);
        store.displayCycleName.setName(emailCycleDetails.displayCycleName.name);
        store.senderEmail.setValue(emailCycleDetails.senderEmail.email);
        store.senderName.setName(emailCycleDetails.senderName.name);
        store.emailContent.subject.setName(
          emailCycleDetails.emailContent.subject.name,
        );
        store.emailContent.body.setContent(
          emailCycleDetails.emailContent.body.content,
        );
        store.status = emailCycleDetails.status;
        store.scheduledAt = emailCycleDetails.scheduledAt.timestamp;

        const scheduleTypeSnapshot =
          emailCycleDetails.scheduleType instanceof ScheduleType.SingleTime
            ? getSingleTimeScheduleSnapshot(emailCycleDetails.scheduleType)
            : emailCycleDetails.scheduleType instanceof ScheduleType.Recurring
              ? getRecurringScheduleSnapshot(emailCycleDetails.scheduleType)
              : getImmediateScheduleSnapshot();
        store.scheduleType =
          scheduleTypeSnapshot.type === "SINGLE_TIME_SCHEDULE"
            ? createSingleTimeScheduleModel(scheduleTypeSnapshot)
            : scheduleTypeSnapshot.type === "RECURRING_SCHEDULE"
              ? createRecurringScheduleModel(scheduleTypeSnapshot)
              : createImmediateScheduleModel(scheduleTypeSnapshot);

        store.isScheduleEditable = emailCycleDetails.isScheduleEditable;

        if (syncUpdateStore) {
          store.updateEmailCycleStore.setScheduleId(emailCycleDetails.id.uuid);
          store.updateEmailCycleStore.setTemplateId(null);
          store.updateEmailCycleStore.templateName.setName(EMPTY_CHARACTER);
          store.updateEmailCycleStore.cycleName.setName(
            emailCycleDetails.cycleName.name,
          );
          store.updateEmailCycleStore.senderEmail.setValue(
            emailCycleDetails.senderEmail.email,
          );
          store.updateEmailCycleStore.senderName.setName(
            emailCycleDetails.senderName.name,
          );
          store.updateEmailCycleStore.emailContent.subject.setName(
            emailCycleDetails.emailContent.subject.name,
          );
          store.updateEmailCycleStore.emailContent.body.setContent(
            emailCycleDetails.emailContent.body.content,
          );
          store.updateEmailCycleStore.scheduleType =
            scheduleTypeSnapshot.type === "SINGLE_TIME_SCHEDULE"
              ? createSingleTimeScheduleModel(scheduleTypeSnapshot)
              : scheduleTypeSnapshot.type === "RECURRING_SCHEDULE"
                ? createRecurringScheduleModel(scheduleTypeSnapshot)
                : createImmediateScheduleModel(scheduleTypeSnapshot);
        }
      } finally {
        store.loadingEmailCycleDetails = false;
      }
    }),

    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createEmailCycleDetailsStore = (): Instance<
  typeof EmailCycleDetailsStore
> => EmailCycleDetailsStore.create(getInitialState());
